import { forwardRef, useEffect, useRef, useCallback, useState, useLayoutEffect } from 'react';
import type {
  FunctionComponent,
  PropsWithoutRef,
  Ref,
  MouseEvent,
  KeyboardEvent as ReactKeyboardEvent,
  FocusEvent as ReactFocusEvent
} from 'react';

import Progress, { type ProgressProps } from '../Progress';
import { focusNonInteractiveElement, getActiveElement, isInstance } from '../../utils';
import Backdrop from '../Backdrop';
import Flex from '../Flex';
import Button from '../Button';
import type { ForwardProps } from '../../types';
import {
  useConsolidatedRef,
  useContentTabIndex,
  useI18n,
  useOuterEvent,
  useUID,
  useFocusTrap,
  usePrevious,
  useModalContext,
  useModalManager,
  useEvent,
  useEscape,
  useTheme
} from '../../hooks';
import Icon, { registerIcon } from '../Icon';
import * as timesIcon from '../Icon/icons/times.icon';
import * as minusIcon from '../Icon/icons/minus.icon';
import * as dockIcon from '../Icon/icons/dock.icon';
import * as undockIcon from '../Icon/icons/undock.icon';
import Text from '../Text';
import { Count } from '../Badges';
import ResponsiveActions from '../ResponsiveActions';
import { getCSSTimeInMilliseconds } from '../../styles';

import MinimizedModal from './MinimizedModal';
import type { ModalProps } from './Modal.types';
import {
  StyledModal,
  StyledModalHeader,
  StyledModalContent,
  StyledModalChildrenWrap
} from './Modal.styles';
import useModalMove from './useModalMove';

registerIcon(timesIcon, minusIcon, dockIcon, undockIcon);

const Modal: FunctionComponent<ModalProps & ForwardProps> = forwardRef(function Modal(
  props: PropsWithoutRef<ModalProps>,
  ref: Ref<HTMLElement>
) {
  const {
    as = 'div',
    progress = false,
    heading,
    headingTag = 'h2',
    icon,
    count,
    children,
    actions,
    stretch = false,
    center = false,
    defaultFocus,
    minimizedLabel,
    onBeforeOpen,
    onAfterOpen,
    onBeforeClose,
    onAfterClose,
    onRequestDismiss,
    onRequestMinimize,
    onRequestMaximize,
    onRequestDock,
    onRequestActivate,
    ...restProps
  } = props;

  const {
    alert,
    dismissible,
    minimizable,
    maximizable,
    dockable,
    moveable: moveableOption,
    state,
    dismiss,
    minimize,
    maximize,
    dock,
    unmount,
    top
  } = useModalContext();

  const modalRef = useConsolidatedRef(ref);
  const modalHeaderRef = useRef(null);
  const modalHeadingRef = useRef<HTMLHeadingElement>(null);
  const modalContentRef = useRef<HTMLDivElement>(null);
  const modalActionsRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const previousState = usePrevious(state);
  const id = useUID();
  const t = useI18n();
  const [progressMessage, setProgressMessage] = useState<ProgressProps['message']>(undefined);
  const [isCloseButtonDisabled, setIsCloseButtonDisabled] = useState(true);
  const theme = useTheme();
  const { lastFocusedBeforeProgress } = useModalManager();
  const previousProgress = usePrevious(progress);
  const lastFocusedInModalRef = useRef<Element | null>(null);

  // Capture the focused element before the inert attribute is committed to the DOM.
  useLayoutEffect(() => {
    const el = getActiveElement();
    if (!previousProgress && progress) {
      lastFocusedBeforeProgress.current = modalRef.current?.contains(el)
        ? el
        : lastFocusedInModalRef.current;
    } else if (
      previousProgress &&
      !progress &&
      (el === lastFocusedBeforeProgress.current || !lastFocusedBeforeProgress.current?.isConnected)
    ) {
      lastFocusedBeforeProgress.current = null;
    }
  }, [previousProgress, progress]);

  const contentTabIndex = useContentTabIndex(modalRef, modalContentRef, [], [progress]);

  const tryDismiss = () => {
    if (onRequestDismiss?.() !== false) {
      dismiss();
    }
  };

  const setFocus = () => {
    if (progress) return;

    if (defaultFocus?.current) {
      defaultFocus.current.focus();
    } else if (modalHeadingRef.current) {
      focusNonInteractiveElement(
        modalHeadingRef.current,
        typeof heading === 'string' ? heading : undefined
      );
    } else if (modalRef.current) {
      focusNonInteractiveElement(
        modalRef.current,
        typeof heading === 'string' ? heading : undefined
      );
    }
  };

  useFocusTrap(modalRef, false);
  useEvent('shortcut', e => e.stopPropagation(), { eventOptions: { capture: true } });

  useOuterEvent('mousedown', [modalRef], e => {
    if (['minimized', 'docked', 'closed'].includes(state)) return;

    if (dismissible) {
      if (e.target === backdropRef.current) {
        tryDismiss();
      }
    } else {
      modalRef.current?.focus();
    }
  });

  useEffect(() => {
    if (state === 'minimized') return;
    if (state === 'open' || state === 'maximized' || state === 'docked') setFocus();
    if (state === 'docked') setIsCloseButtonDisabled(false);

    // This won't actually run until potential changes to docked modal handling.
    if (state === 'closed' && previousState === 'docked') {
      onBeforeClose?.();
      unmount();
      onAfterClose?.();
    }
  }, [state]);

  useEffect(() => {
    // If a modal is already in a closed state on initial mount, unmount it.
    // This only happens when a docked modal closes since the ModalManager
    // remounts it outside of the DockedModals container if state !== 'docked'.
    if (state === 'closed') {
      onBeforeClose?.();
      unmount();
      onAfterClose?.();
    }
  }, []);

  // Dismiss on Escape needs to be handled using React event handling to be cancelable using SyntheticEvent.stopPropagation.
  const onModalKeyDown = useCallback(
    (e: ReactKeyboardEvent) => {
      if (dismissible && (state === 'open' || state === 'maximized') && e.key === 'Escape' && top) {
        tryDismiss();
      }
    },
    [dismissible, state, top, tryDismiss]
  );

  // Document-level handler as fallback when focus is on body or outside modal
  useEscape(
    (e: KeyboardEvent) => {
      if (
        dismissible &&
        top &&
        (state === 'open' || state === 'maximized') &&
        isInstance(e.target, Node) &&
        !modalRef.current?.contains(e.target)
      ) {
        tryDismiss();
      }
    },
    dismissible && top && (state === 'open' || state === 'maximized') ? document : null,
    [dismissible, top, state, tryDismiss]
  );

  const onHeaderActionKeyDown = useCallback((e: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (e.repeat && (e.key === 'Enter' || e.key === ' ')) e.preventDefault();
  }, []);

  useEffect(() => {
    if (!progress) {
      setFocus();
    }

    if (typeof progress === 'object' && progress.message !== progressMessage) {
      setProgressMessage(progress.message);
    } else if (progress === true) {
      setProgressMessage(t('loading'));
    }
  }, [progress]);

  // Animate enter and exit
  useLayoutEffect(() => {
    if (!modalRef.current) return;

    const translate = ['0 -4rem', '0 0'];
    const options: KeyframeAnimationOptions = {
      // Multiplier needed to coordinate with Backdrop's transition speed multiplier
      duration: getCSSTimeInMilliseconds(theme.base.animation.speed) * 2,
      easing: theme.base.animation.timing.ease,
      fill: 'forwards'
    };

    let animation: Animation | undefined;

    if (state === 'open') {
      animation = modalRef.current.animate({ translate }, options);
    } else if (state === 'closed' && previousState === 'open') {
      animation = modalRef.current.animate({ translate: translate.reverse() }, options);
    }

    // Handler will not leak since animation object is garbage collected
    animation?.addEventListener('finish', () => {
      // Removes the element from the compositor.
      animation.cancel();
    });
  }, [state]);

  const maximized = stretch || state === 'maximized';

  // Only enable useModalMove in docked state here since MinimizedModal handles calling when minimized
  const moveable = state === 'docked' && !!moveableOption;
  useModalMove({
    enabled: moveable,
    moveRef: modalRef,
    grabRef: modalHeaderRef
  });

  const modal = (
    <Flex
      {...restProps}
      container={{ direction: 'column' }}
      ref={modalRef}
      as={StyledModal}
      forwardedAs={as}
      onClick={(e: MouseEvent) => e.stopPropagation()}
      onBlur={(e: ReactFocusEvent) => {
        if (isInstance(e.target, Element)) lastFocusedInModalRef.current = e.target;
      }}
      onKeyDown={onModalKeyDown}
      alert={alert}
      role={alert ? 'alertdialog' : 'dialog'}
      stretch={maximized}
      center={center}
      aria-labelledby={`${id}-heading`}
      aria-describedby={alert ? `${id}-content` : undefined}
      aria-modal={state !== 'docked'}
      state={state}
      aria-busy={!!progress}
      data-moveable={moveable ? '' : undefined}
    >
      <Flex
        container={{ justify: 'between', alignItems: 'start', gap: 1 }}
        item={{ shrink: 0 }}
        as={StyledModalHeader}
        role='none'
        ref={modalHeaderRef}
        tabIndex={moveable ? 0 : undefined}
      >
        <Flex
          container={{ alignItems: 'center', gap: 1 }}
          as={Text}
          variant={headingTag}
          id={`${id}-heading`}
          ref={modalHeadingRef}
        >
          <span>{heading}</span>
          {typeof count === 'number' && <Count>{count}</Count>}
          {typeof count === 'object' && <Count hasMore={count.hasMore}>{count.value}</Count>}
        </Flex>

        <Flex container>
          {maximizable && !alert && state !== 'maximized' && (
            <Button
              icon
              variant='simple'
              label={t('maximize')}
              aria-label={t('modal_maximize')}
              onKeyDown={onHeaderActionKeyDown}
              onClick={() => {
                if (onRequestMaximize?.() !== false) {
                  maximize();
                }
              }}
            >
              <Icon name='undock' />
            </Button>
          )}
          {dockable && !alert && state !== 'docked' && (
            <Button
              icon
              variant='simple'
              label={t('dock')}
              aria-label={t('modal_dock')}
              onKeyDown={onHeaderActionKeyDown}
              onClick={() => {
                if (onRequestDock?.() !== false) {
                  dock();
                }
              }}
            >
              <Icon name='dock' />
            </Button>
          )}
          {minimizable && !alert && (
            <Button
              icon
              variant='simple'
              label={t('minimize')}
              aria-label={t('modal_minimize')}
              onKeyDown={onHeaderActionKeyDown}
              onClick={() => {
                if (onRequestMinimize?.() !== false) {
                  minimize();
                }
              }}
            >
              <Icon name='minus' />
            </Button>
          )}
          {dismissible && !alert && (
            <Button
              icon
              variant='simple'
              label={t('close')}
              aria-label={t('modal_close')}
              onKeyDown={onHeaderActionKeyDown}
              onClick={() => {
                if (!isCloseButtonDisabled) {
                  tryDismiss();
                }
              }}
            >
              <Icon name='times' />
            </Button>
          )}
        </Flex>
      </Flex>

      <Flex
        item={{ grow: 1 }}
        as={StyledModalContent}
        ref={modalContentRef}
        tabIndex={contentTabIndex}
        style={
          modalContentRef.current
            ? { '--scroll-top': `${modalContentRef.current.scrollTop}px` }
            : undefined
        }
      >
        {children && (
          <StyledModalChildrenWrap id={`${id}-content`} inert={progress ? '' : undefined}>
            {children}
          </StyledModalChildrenWrap>
        )}

        <Progress
          visible={!!progress}
          focusOnVisible
          delay
          placement='local'
          message={progressMessage}
          onTransitionEndOut={() => setProgressMessage(undefined)}
        />
      </Flex>

      {actions && (
        <ResponsiveActions
          as='footer'
          role='none'
          actionsRef={modalActionsRef}
          actionsEl={actions}
        />
      )}
    </Flex>
  );

  const renderModal =
    state === 'docked' ? (
      modal
    ) : (
      <Backdrop
        ref={backdropRef}
        container={{
          alignItems: center || maximized ? 'center' : 'start'
        }}
        transitionSpeed='slow'
        open={state === 'open' || state === 'maximized'}
        onBeforeTransitionIn={onBeforeOpen}
        onAfterTransitionIn={() => {
          setIsCloseButtonDisabled(false);
          onAfterOpen?.();
        }}
        onBeforeTransitionOut={onBeforeClose}
        onAfterTransitionOut={() => {
          unmount();
          onAfterClose?.();
        }}
      >
        {modal}
      </Backdrop>
    );

  return state === 'minimized' ? (
    <MinimizedModal {...props} {...restProps} heading={minimizedLabel ?? heading} icon={icon} />
  ) : (
    renderModal
  );
});

export default Modal;
